import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "react-query";
import styled from "styled-components";
import { motion } from "framer-motion";
import {
  FiSearch,
  FiFilter,
  FiGrid,
  FiList,
  FiClock,
  FiUsers,
  FiMapPin,
  FiTruck,
  FiCalendar,
  FiDollarSign,
} from "react-icons/fi";
import { mealPlansAPI } from "../../services/api";
import { useAuth } from "../../contexts/AuthContext";

const MealPlansContainer = styled.div`
  min-height: 100vh;
  background: #f8f9fa;
  padding: 2rem 0;
`;

const MealPlansContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;

  @media (max-width: 768px) {
    padding: 0 0.75rem;
  }

  @media (max-width: 480px) {
    padding: 0 0.5rem;
  }
`;

const PageHeader = styled.div`
  text-align: center;
  margin-bottom: 3rem;

  h1 {
    font-size: 2.5rem;
    font-weight: 700;
    color: #2d5016;
    margin-bottom: 1rem;
  }

  p {
    font-size: 1.125rem;
    color: #666;
    max-width: 600px;
    margin: 0 auto;
  }
`;

const SearchAndFilters = styled.div`
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    padding: 1rem;
    margin-bottom: 1.5rem;
  }

  @media (max-width: 480px) {
    padding: 0.75rem;
    margin-bottom: 1rem;
  }
`;

const SearchBar = styled.div`
  position: relative;
  margin-bottom: 1rem;

  input {
    width: 100%;
    padding: 0.75rem 1rem;
    padding-left: 2.5rem;
    border: 2px solid #e0e0e0;
    border-radius: 8px;
    font-size: 1rem;
    transition: border-color 0.3s ease;

    &:focus {
      outline: none;
      border-color: #2d5016;
    }
  }

  .search-icon {
    position: absolute;
    left: 0.75rem;
    top: 50%;
    transform: translateY(-50%);
    color: #666;
    font-size: 1.125rem;
  }
`;

const FiltersRow = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 1rem;
  }
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  min-width: 150px;
  flex: 1;

  @media (max-width: 768px) {
    min-width: unset;
    width: 100%;
  }

  label {
    font-size: 0.875rem;
    font-weight: 500;
    color: #333;
  }

  select {
    padding: 0.5rem;
    border: 2px solid #e0e0e0;
    border-radius: 6px;
    font-size: 0.875rem;
    background: white;
    width: 100%;

    &:focus {
      outline: none;
      border-color: #2d5016;
    }
  }
`;

const PriceRange = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  min-width: 200px;
  flex: 1;

  @media (max-width: 768px) {
    min-width: unset;
    width: 100%;
  }

  label {
    font-size: 0.875rem;
    font-weight: 500;
    color: #333;
  }

  .price-inputs {
    display: flex;
    gap: 0.5rem;
    align-items: center;
    width: 100%;

    @media (max-width: 480px) {
      flex-direction: column;
      align-items: stretch;
      gap: 0.75rem;
    }

    input {
      flex: 1;
      padding: 0.5rem;
      border: 2px solid #e0e0e0;
      border-radius: 6px;
      font-size: 0.875rem;
      min-width: 0; /* Allow input to shrink below content size */
      width: 100%;

      &:focus {
        outline: none;
        border-color: #2d5016;
      }

      @media (max-width: 480px) {
        width: 100%;
      }
    }

    span {
      color: #666;
      font-size: 0.875rem;
      white-space: nowrap;

      @media (max-width: 480px) {
        text-align: center;
      }
    }
  }
`;

const ViewToggle = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-left: auto;

  @media (max-width: 768px) {
    margin-left: 0;
    justify-content: center;
    margin-top: 0.5rem;
  }

  button {
    padding: 0.5rem;
    border: 2px solid #e0e0e0;
    background: white;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.3s ease;
    min-width: 40px;

    &.active {
      background: #2d5016;
      color: white;
      border-color: #2d5016;
    }

    &:hover:not(.active) {
      border-color: #2d5016;
    }
  }
`;

const MealPlansGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 2rem;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }

  @media (max-width: 480px) {
    gap: 1rem;
  }

  &.list-view {
    grid-template-columns: 1fr;
  }
`;

const MealPlanCard = styled(motion.div)`
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;

  &:hover {
    transform: translateY(-5px);
  }
`;

const MealPlanImage = styled.div`
  height: 200px;
  background: linear-gradient(135deg, #2d5016, #4a7c59);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 3rem;
  position: relative;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .placeholder {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.5rem;
  }

  .plan-type-badge {
    position: absolute;
    top: 1rem;
    right: 1rem;
    background: rgba(255, 255, 255, 0.9);
    color: #2d5016;
    padding: 0.25rem 0.75rem;
    border-radius: 20px;
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
  }
`;

const MealPlanInfo = styled.div`
  padding: 1.5rem;

  h3 {
    font-size: 1.25rem;
    font-weight: 600;
    color: #2d5016;
    margin-bottom: 0.5rem;
  }

  .vendor-info {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    color: #666;
    font-size: 0.875rem;
    margin-bottom: 1rem;

    .vendor-name {
      font-weight: 500;
      color: #333;
    }
  }

  .description {
    color: #666;
    font-size: 0.875rem;
    line-height: 1.5;
    margin-bottom: 1rem;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .plan-details {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
    margin-bottom: 1rem;

    .detail-item {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      color: #666;
      font-size: 0.875rem;

      .icon {
        color: #2d5016;
      }
    }
  }

  .price-section {
    margin-bottom: 1rem;
    padding-top: 1rem;
    border-top: 1px solid #e0e0e0;

    .pricing-options {
      display: flex;
      flex-direction: column;
      gap: 0.75rem;
    }

    .price-option {
      display: flex;
      flex-direction: column;
      gap: 0.25rem;
    }

    .price-label {
      font-size: 0.875rem;
      font-weight: 600;
      color: #2d5016;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }

    .price {
      font-size: 1.5rem;
      font-weight: 700;
      color: #2d5016;
    }

    .price-breakdown {
      font-size: 0.75rem;
      color: #666;
      font-style: italic;
    }

    .price-note {
      font-size: 0.75rem;
      color: #666;
    }

    .price-period {
      color: #666;
      font-size: 0.875rem;
    }
  }

  .fulfillment-options {
    margin-bottom: 1rem;
    display: flex;
    gap: 0.5rem;
    flex-wrap: wrap;
  }

  .fulfillment-badge {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0.25rem 0.75rem;
    border-radius: 20px;
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;

    .icon {
      font-size: 0.875rem;
    }

    &.both {
      background: linear-gradient(135deg, #2d5016, #4a7c59);
      color: white;
    }

    &.delivery-only {
      background: #e8f5e8;
      color: #2d5016;
      border: 1px solid #2d5016;
    }

    &.pickup-only {
      background: #fff3e0;
      color: #e65100;
      border: 1px solid #e65100;
    }
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-top: 1rem;
`;

const SubscribeButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  padding: 0.75rem;
  background: #2d5016;
  color: white;
  text-decoration: none;
  border-radius: 6px;
  font-weight: 500;
  transition: background 0.3s ease;

  &:hover {
    background: #1a3a0e;
  }
`;

const ViewDetailsButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  padding: 0.75rem;
  background: white;
  color: #2d5016;
  text-decoration: none;
  border: 2px solid #2d5016;
  border-radius: 6px;
  font-weight: 500;
  transition: all 0.3s ease;

  &:hover {
    background: #2d5016;
    color: white;
  }
`;

const LoadingState = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  font-size: 1.125rem;
  color: #666;
`;

const ErrorState = styled.div`
  text-align: center;
  padding: 3rem;
  color: #dc3545;

  h3 {
    margin-bottom: 1rem;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem;
  color: #666;

  h3 {
    margin-bottom: 1rem;
    color: #333;
  }
`;

const MealPlans = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [vendorFilter, setVendorFilter] = useState("");
  const [planTypeFilter, setPlanTypeFilter] = useState("");
  const [cuisineFilter, setCuisineFilter] = useState("");
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [viewMode, setViewMode] = useState("grid");
  const { isAuthenticated } = useAuth();

  const { data, isLoading, error } = useQuery(
    [
      "mealPlans",
      { vendorFilter, planTypeFilter, cuisineFilter, minPrice, maxPrice },
    ],
    () =>
      mealPlansAPI.getMealPlans({
        vendorId: vendorFilter,
        planType: planTypeFilter,
        cuisine: cuisineFilter,
        minPrice: minPrice || undefined,
        maxPrice: maxPrice || undefined,
      }),
    {
      keepPreviousData: true,
    }
  );

  const mealPlans = data?.data?.data?.mealPlans || [];

  // Filter meal plans by search term
  const filteredMealPlans = mealPlans.filter(
    (plan) =>
      plan.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      plan.business_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      plan.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const planTypes = ["weekly", "monthly"];
  const cuisineTypes = [
    "Guyanese",
    "Indian",
    "Chinese",
    "Caribbean",
    "Creole",
    "International",
    "Mixed",
  ];

  if (isLoading) {
    return (
      <MealPlansContainer>
        <MealPlansContent>
          <PageHeader>
            <h1>Meal Plans</h1>
            <p>Discover delicious meal subscription plans from local vendors</p>
          </PageHeader>
          <LoadingState>
            <div className="loading-spinner" />
            Loading meal plans...
          </LoadingState>
        </MealPlansContent>
      </MealPlansContainer>
    );
  }

  if (error) {
    return (
      <MealPlansContainer>
        <MealPlansContent>
          <PageHeader>
            <h1>Meal Plans</h1>
            <p>Discover delicious meal subscription plans from local vendors</p>
          </PageHeader>
          <ErrorState>
            <h3>Something went wrong</h3>
            <p>Unable to load meal plans. Please try again later.</p>
          </ErrorState>
        </MealPlansContent>
      </MealPlansContainer>
    );
  }

  return (
    <MealPlansContainer>
      <MealPlansContent>
        <PageHeader>
          <h1>Meal Plans</h1>
          <p>Discover delicious meal subscription plans from local vendors</p>
        </PageHeader>

        <SearchAndFilters>
          <SearchBar>
            <FiSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search meal plans..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </SearchBar>

          <FiltersRow>
            <FilterGroup>
              <label>Plan Type</label>
              <select
                value={planTypeFilter}
                onChange={(e) => setPlanTypeFilter(e.target.value)}
              >
                <option value="">All Plans</option>
                {planTypes.map((type) => (
                  <option key={type} value={type}>
                    {type.charAt(0).toUpperCase() + type.slice(1)}
                  </option>
                ))}
              </select>
            </FilterGroup>

            <FilterGroup>
              <label>Cuisine</label>
              <select
                value={cuisineFilter}
                onChange={(e) => setCuisineFilter(e.target.value)}
              >
                <option value="">All Cuisines</option>
                {cuisineTypes.map((cuisine) => (
                  <option key={cuisine} value={cuisine}>
                    {cuisine}
                  </option>
                ))}
              </select>
            </FilterGroup>

            <PriceRange>
              <label>Price Range (GYD)</label>
              <div className="price-inputs">
                <input
                  type="number"
                  placeholder="Min"
                  value={minPrice}
                  onChange={(e) => setMinPrice(e.target.value)}
                />
                <span>to</span>
                <input
                  type="number"
                  placeholder="Max"
                  value={maxPrice}
                  onChange={(e) => setMaxPrice(e.target.value)}
                />
              </div>
            </PriceRange>

            <ViewToggle>
              <button
                className={viewMode === "grid" ? "active" : ""}
                onClick={() => setViewMode("grid")}
                aria-label="Grid view"
              >
                <FiGrid />
              </button>
              <button
                className={viewMode === "list" ? "active" : ""}
                onClick={() => setViewMode("list")}
                aria-label="List view"
              >
                <FiList />
              </button>
            </ViewToggle>
          </FiltersRow>
        </SearchAndFilters>

        {filteredMealPlans.length === 0 ? (
          <EmptyState>
            <h3>No meal plans found</h3>
            <p>Try adjusting your search criteria or check back later.</p>
          </EmptyState>
        ) : (
          <MealPlansGrid className={viewMode === "list" ? "list-view" : ""}>
            {filteredMealPlans.map((plan, index) => (
              <MealPlanCard
                key={plan.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <MealPlanImage>
                  {plan.image_url ? (
                    <img src={plan.image_url} alt={plan.title} />
                  ) : (
                    <div className="placeholder">
                      <span>🍽️</span>
                      <span>{plan.title.charAt(0)}</span>
                    </div>
                  )}
                  <div className="plan-type-badge">{plan.plan_type}</div>
                </MealPlanImage>
                <MealPlanInfo>
                  <h3>{plan.title}</h3>

                  <div className="vendor-info">
                    <span>by</span>
                    <span className="vendor-name">{plan.business_name}</span>
                  </div>

                  {plan.description && (
                    <div className="description">{plan.description}</div>
                  )}

                  <div className="plan-details">
                    <div className="detail-item">
                      <FiCalendar className="icon" />
                      <span>{plan.duration_days} days</span>
                    </div>
                    <div className="detail-item">
                      <FiMapPin className="icon" />
                      <span>{plan.business_address}</span>
                    </div>
                    <div className="detail-item">
                      <FiClock className="icon" />
                      <span>{plan.cuisine_type}</span>
                    </div>
                  </div>

                  <div className="fulfillment-options">
                    {(() => {
                      const offersDelivery =
                        plan.offers_delivery === true ||
                        plan.offers_delivery === "true" ||
                        plan.offers_delivery === 1 ||
                        plan.offersDelivery === true ||
                        plan.offersDelivery === "true" ||
                        plan.offersDelivery === 1;
                      const offersPickup =
                        plan.offers_pickup === true ||
                        plan.offers_pickup === "true" ||
                        plan.offers_pickup === 1 ||
                        plan.offersPickup === true ||
                        plan.offersPickup === "true" ||
                        plan.offersPickup === 1;

                      if (offersDelivery && offersPickup) {
                        return (
                          <div className="fulfillment-badge both">
                            <FiTruck className="icon" />
                            <span>Delivery & Pickup</span>
                          </div>
                        );
                      } else if (offersDelivery && !offersPickup) {
                        return (
                          <div className="fulfillment-badge delivery-only">
                            <FiTruck className="icon" />
                            <span>Delivery Only</span>
                          </div>
                        );
                      } else if (!offersDelivery && offersPickup) {
                        return (
                          <div className="fulfillment-badge pickup-only">
                            <FiMapPin className="icon" />
                            <span>Pickup Only</span>
                          </div>
                        );
                      }
                      return null;
                    })()}
                  </div>

                  <div className="price-section">
                    <div className="pricing-options">
                      {(() => {
                        const offersDelivery =
                          plan.offers_delivery === true ||
                          plan.offers_delivery === "true" ||
                          plan.offers_delivery === 1 ||
                          plan.offersDelivery === true ||
                          plan.offersDelivery === "true" ||
                          plan.offersDelivery === 1;
                        const offersPickup =
                          plan.offers_pickup === true ||
                          plan.offers_pickup === "true" ||
                          plan.offers_pickup === 1 ||
                          plan.offersPickup === true ||
                          plan.offersPickup === "true" ||
                          plan.offersPickup === 1;
                        const basePrice =
                          parseFloat(
                            plan.base_price ||
                              plan.basePrice ||
                              plan.price ||
                              0
                          );
                        const deliveryFee =
                          parseFloat(
                            plan.delivery_fee || plan.deliveryFee || 0
                          );

                        if (offersDelivery) {
                          return (
                            <div className="price-option">
                              <div className="price-label">Delivery</div>
                              <div className="price">
                                GYD ${Math.round(basePrice + deliveryFee).toLocaleString()}
                              </div>
                              {deliveryFee > 0 && (
                                <div className="price-breakdown">
                                  Base: ${Math.round(basePrice).toLocaleString()}{" "}
                                  + Delivery: ${Math.round(deliveryFee).toLocaleString()}
                                </div>
                              )}
                            </div>
                          );
                        }
                        if (offersPickup) {
                          return (
                            <div className="price-option">
                              <div className="price-label">Pickup</div>
                              <div className="price">
                                GYD ${Math.round(basePrice).toLocaleString()}
                              </div>
                              <div className="price-note">
                                Pickup from{" "}
                                {plan.pickup_address ||
                                  plan.pickupAddress ||
                                  "vendor location"}
                              </div>
                            </div>
                          );
                        }
                        return (
                          <div className="price-option">
                            <div className="price">
                              GYD ${Math.round(parseFloat(plan.price || 0)).toLocaleString()}
                            </div>
                            <div className="price-period">
                              per {plan.plan_type || plan.planType}
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  </div>

                  <ActionButtons>
                    <ViewDetailsButton to={`/meal-plans/${plan.id}`}>
                      View Details
                    </ViewDetailsButton>
                    <SubscribeButton
                      to={isAuthenticated ? `/checkout/${plan.id}` : "/login"}
                    >
                      {isAuthenticated ? "Purchase" : "Login"}
                    </SubscribeButton>
                  </ActionButtons>
                </MealPlanInfo>
              </MealPlanCard>
            ))}
          </MealPlansGrid>
        )}
      </MealPlansContent>
    </MealPlansContainer>
  );
};

export default MealPlans;
